import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { formatCurrency } from "../../config/market";
import { mobile } from "../../responsive";
import {
  stepIndexForStatus,
  stepDetail,
  visibleSteps,
  isCancelled,
} from "../../utils/orderStatus";
import { orderTotals } from "../../utils/orderTotals";
import { formatBuyerSummary } from "../../utils/buyerIdentity";
import {
  describeCreatedOrderDeliveryTime,
  readPickedSchedule,
} from "../../utils/deliverySchedule";

/**
 * OrderConfirmation — the ONE post-payment order summary.
 *
 * Six pages (Airtel, MoMo, COD, COD-RW, Card, Healthcare) each carried their own
 * copy of this layout, their own step array and their own totals maths. They had
 * drifted apart in both copy and correctness. This is the single presentation;
 * pages supply the order, a payment-specific slot, and the continue handler.
 *
 * It is deliberately presentational — no redux, no sockets, no fetching. Each
 * page keeps its own data flow and simply hands the resulting order down.
 */
const OrderConfirmation = ({
  order,
  paymentLabel,
  paymentSlot,
  onContinueShopping,
  continueLabel,
}) => {
  const totals = orderTotals(order);
  const status = order?.delivery_status;
  const activeStep = stepIndexForStatus(status);
  const steps = visibleSteps(status);
  const cancelled = isCancelled(status);
  const address = order?.shippingAddress;
  const addressLine =
    address?.locationName?.name ||
    address?.address?.address ||
    address?.description ||
    null;
  const addressVicinity = address?.locationName?.vicinity || null;
  // Shown only when the order carried a buyer tax id; a walk-in has none.
  const buyerSummary = formatBuyerSummary(order?.buyer);
  /* The delivery time this order was placed for. The created order is the
     source of truth; when the body read back here predates `scheduledDelivery`
     we fall back to the window the customer picked at checkout, and only when
     it was remembered for THIS order. Express orders show nothing. */
  const deliveryTiming = React.useMemo(
    () => describeCreatedOrderDeliveryTime(order, readPickedSchedule()),
    [order]
  );

  return (
    <Page>
      <Hero $cancelled={cancelled}>
        <HeroRow>
          <Mark $cancelled={cancelled} aria-hidden="true">
            {cancelled ? (
              <svg viewBox="0 0 24 24"><path d="M7 7l10 10M17 7L7 17" /></svg>
            ) : (
              <svg viewBox="0 0 24 24"><path d="M4 12.5l5.2 5.2L20 7" /></svg>
            )}
          </Mark>
          <div>
            <HeroTitle>{cancelled ? "Order cancelled" : "Order placed"}</HeroTitle>
            <HeroSub>
              {cancelled
                ? "This order will not be delivered. Any payment taken is refunded to your Lusuku wallet."
                : "We've sent it to the vendor. You'll be notified as it moves."}
            </HeroSub>
          </div>
        </HeroRow>
        <HeroMeta>
          {order?._id && (
            <div>
              <dt>Order</dt>
              <dd>{String(order._id).slice(-8).toUpperCase()}</dd>
            </div>
          )}
          {paymentLabel && (
            <div>
              <dt>Payment</dt>
              <dd>{paymentLabel}</dd>
            </div>
          )}
          {status && (
            <div>
              <dt>Status</dt>
              <dd>{String(status).replace(/_/g, " ")}</dd>
            </div>
          )}
          {order?.fulfilmentOutlet?.name && (
            <div>
              <dt>Fulfilled from</dt>
              <dd title={order.fulfilmentOutlet.address || undefined}>
                {order.fulfilmentOutlet.name}
              </dd>
            </div>
          )}
          <div>
            <dt>Total</dt>
            <dd>{formatCurrency(totals.total)}</dd>
          </div>
        </HeroMeta>
      </Hero>

      {paymentSlot && <Card>{paymentSlot}</Card>}

      <Card>
        <CardTitle>Tracking</CardTitle>
        <Track>
          {steps.map((label, i) => {
            const state =
              activeStep === null ? "" : i < activeStep ? "done" : i === activeStep ? "now" : "";
            return (
              <Step key={label} $state={state}>
                <Dot $state={state}>
                  {state === "done" ? (
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12.5l5.2 5.2L20 7" /></svg>
                  ) : (
                    i + 1
                  )}
                </Dot>
                <StepName>{label}</StepName>
                {state === "now" && <StepNote>{stepDetail(i)}</StepNote>}
              </Step>
            );
          })}
        </Track>
      </Card>

      <Columns>
        <Card>
          <CardTitle>Your order</CardTitle>
          {(order?.orderItems || []).map((item) => (
            <ItemRow key={item._id || item.name}>
              <Thumb>
                {item.imageUrl || item.image ? (
                  <img src={item.imageUrl || item.image} alt={item.name} />
                ) : null}
              </Thumb>
              <ItemInfo>
                <ItemName>{item.name}</ItemName>
                <ItemMeta>
                  {item.qty ?? item.quantity} &times; {formatCurrency(item.price)}
                </ItemMeta>
              </ItemInfo>
              <ItemSum>
                {formatCurrency((item.qty ?? item.quantity ?? 0) * (item.price ?? 0))}
              </ItemSum>
            </ItemRow>
          ))}
          {!order?.orderItems?.length && <Empty>No items on this order.</Empty>}

          {/* A grand total with no explanation is why a 3,000 drink reads as
              8,000. Show the parts whenever they add up to the whole. */}
          {totals.hasBreakdown ? (
            <Totals>
              <CostRow>
                <span>Items</span>
                <span>{formatCurrency(totals.itemsSubtotal)}</span>
              </CostRow>
              <CostRow>
                <span>
                  Delivery
                  {totals.distanceKm !== null && <Tag title="Road distance from the vendor">{totals.distanceKm.toFixed(1)} km</Tag>}
                </span>
                <span>{formatCurrency(totals.deliveryFee)}</span>
              </CostRow>
              {totals.overageFee > 0 && (
                <CostRow $accent>
                  <span>
                    Extra distance
                    <Tag $accent title="Charged because the drop-off was further than the address on this order">
                      {totals.overageKm.toFixed(1)} km
                    </Tag>
                  </span>
                  <span>{formatCurrency(totals.overageFee)}</span>
                </CostRow>
              )}
              {totals.processingFee > 0 && (
                <CostRow>
                  <span>
                    {totals.processingLabel}
                    <Tag title="What the mobile money or card network charges to collect this payment. Cash on delivery is never charged one.">
                      payment
                    </Tag>
                  </span>
                  <span>{formatCurrency(totals.processingFee)}</span>
                </CostRow>
              )}
              <GrandRow>
                <b>Total</b>
                <strong>{formatCurrency(totals.total)}</strong>
              </GrandRow>
            </Totals>
          ) : (
            <Totals>
              <GrandRow>
                <b>Total</b>
                <strong>{formatCurrency(totals.total)}</strong>
              </GrandRow>
            </Totals>
          )}
        </Card>

        <Card>
          <CardTitle>Delivery</CardTitle>
          <Detail>
            <dt>Deliver to</dt>
            <dd>
              {addressLine || "Not provided"}
              {addressVicinity && <Muted>{addressVicinity}</Muted>}
            </dd>
          </Detail>
          {deliveryTiming && (
            <Detail>
              <dt>{deliveryTiming.title}</dt>
              <dd>{deliveryTiming.detail}</dd>
            </Detail>
          )}
          {address?.customerNumber && (
            <Detail>
              <dt>Contact</dt>
              <dd>{address.customerNumber}</dd>
            </Detail>
          )}
          {buyerSummary && (
            <Detail>
              <dt>Buyer tax ID</dt>
              <dd>{buyerSummary}</dd>
            </Detail>
          )}
          {order?.deliveryOverage?.appliedAt && (
            <Detail>
              <dt>Note</dt>
              <dd>
                The rider delivered {Number(order.deliveryOverage.billableKm || 0).toFixed(1)} km
                beyond the address on this order, charged at{" "}
                {formatCurrency(order.deliveryOverage.ratePerKm)} per km.
              </dd>
            </Detail>
          )}
          {onContinueShopping && (
            <Cta type="button" onClick={onContinueShopping}>
              {continueLabel || "Continue shopping"}
            </Cta>
          )}
        </Card>
      </Columns>
    </Page>
  );
};

OrderConfirmation.propTypes = {
  order: PropTypes.object,
  paymentLabel: PropTypes.string,
  paymentSlot: PropTypes.node,
  onContinueShopping: PropTypes.func,
  continueLabel: PropTypes.string,
};

/* ── styles ──────────────────────────────────────────────────────────────── */
const INK = "#14211f";
const INK2 = "#4a5a57";
const INK3 = "#8a9996";
const LINE = "#e3e8e6";
const GREEN = "#5d8f24";
const AMBER = "#a35c00";

/* The site Navbar is position:fixed, so it paints ON TOP of this page instead
   of pushing it down — the hero's top (the "Order placed" title, the tick, and
   the ORDER / PAYMENT labels) sat underneath the bar. Navbar publishes its
   real rendered height as --lusuku-nav-h; offset by that plus this page's own
   top padding. The variable matters most on mobile, where the bar stacks into
   three rows and is roughly twice as tall as on desktop. The 130px fallback
   matches the rest of the app and covers the first paint before it is set. */
const Page = styled.div`
  max-width: 940px;
  margin: 0 auto;
  padding: calc(var(--lusuku-nav-h, 130px) + 24px) 20px 64px;
  color: ${INK};
  ${mobile({ padding: "calc(var(--lusuku-nav-h, 130px) + 16px) 12px 48px" })}
`;

const Hero = styled.div`
  background: ${(p) =>
    p.$cancelled
      ? "linear-gradient(135deg,#5b2f2a 0%,#3d1e1a 100%)"
      : "linear-gradient(135deg,#2b4a52 0%,#1e363d 100%)"};
  border-radius: 14px;
  padding: 28px;
  color: #fff;
  ${mobile({ padding: "20px" })}
`;

const HeroRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const Mark = styled.div`
  flex: none;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$cancelled ? "#c0483a" : GREEN)};
  box-shadow: 0 0 0 6px ${(p) => (p.$cancelled ? "rgba(192,72,58,.22)" : "rgba(93,143,36,.22)")};
  svg {
    width: 24px;
    height: 24px;
    fill: none;
    stroke: #fff;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const HeroSub = styled.p`
  margin: 0;
  font-size: 14px;
  color: #cfe0e2;
`;

const HeroMeta = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 26px;
  margin: 20px 0 0;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.14);

  div { min-width: 110px; }
  dt {
    font-size: 11px;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #9fb9bd;
    margin-bottom: 3px;
  }
  dd { margin: 0; font-size: 15px; font-weight: 600; }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${LINE};
  border-radius: 14px;
  padding: 22px;
  margin-top: 16px;
`;

const CardTitle = styled.h2`
  margin: 0 0 18px;
  font-size: 12px;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: ${INK2};
  font-weight: 700;
`;

const Track = styled.div`
  display: flex;
  position: relative;
  ${mobile({ flexDirection: "column", gap: "14px" })}
`;

const Step = styled.div`
  flex: 1;
  text-align: center;
  position: relative;
  ${mobile({ display: "flex", alignItems: "center", gap: "10px", textAlign: "left" })}
`;

const Dot = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  margin: 0 auto 9px;
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: 700;
  background: ${(p) => (p.$state === "done" ? GREEN : "#fff")};
  border: 3px solid
    ${(p) => (p.$state === "done" ? GREEN : p.$state === "now" ? "#f28705" : LINE)};
  color: ${(p) => (p.$state === "done" ? "#fff" : p.$state === "now" ? "#f28705" : INK3)};
  box-shadow: ${(p) => (p.$state === "now" ? "0 0 0 5px #fff4e6" : "none")};
  svg {
    width: 14px; height: 14px; fill: none; stroke: currentColor;
    stroke-width: 3; stroke-linecap: round; stroke-linejoin: round;
  }
  ${mobile({ margin: "0" })}
`;

const StepName = styled.b`
  display: block;
  font-size: 13px;
  font-weight: 600;
`;

const StepNote = styled.span`
  display: block;
  font-size: 11.5px;
  color: ${AMBER};
  margin-top: 2px;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1.45fr 1fr;
  gap: 16px;
  align-items: start;
  ${mobile({ gridTemplateColumns: "1fr" })}
`;

const ItemRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 13px 0;
  border-bottom: 1px solid ${LINE};
  &:last-of-type { border-bottom: 0; }
`;

const Thumb = styled.div`
  width: 54px; height: 54px; border-radius: 10px; flex: none;
  background: #f4f6f5; border: 1px solid ${LINE}; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const ItemInfo = styled.div`flex: 1; min-width: 0;`;
const ItemName = styled.div`font-weight: 600;`;
const ItemMeta = styled.div`font-size: 13px; color: ${INK2}; margin-top: 1px;`;
const ItemSum = styled.div`margin-left: auto; font-weight: 600; font-variant-numeric: tabular-nums;`;
const Empty = styled.div`color: ${INK3}; font-size: 14px; padding: 8px 0;`;

const Totals = styled.div`
  margin-top: 6px;
  padding-top: 14px;
  border-top: 1px dashed ${LINE};
`;

const CostRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 14px;
  color: ${(p) => (p.$accent ? AMBER : INK2)};
  > span:first-child { display: flex; align-items: center; gap: 7px; }
  > span:last-child { font-variant-numeric: tabular-nums; font-weight: 500; color: ${(p) => (p.$accent ? AMBER : INK)}; }
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 700;
  border-radius: 5px;
  padding: 1px 6px;
  cursor: help;
  color: ${(p) => (p.$accent ? AMBER : "#3f6416")};
  background: ${(p) => (p.$accent ? "#fff4e6" : "#f0f6e8")};
  border: 1px solid ${(p) => (p.$accent ? "#f3ddb8" : "#d9e7c4")};
`;

const GrandRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-top: 10px;
  padding-top: 14px;
  border-top: 2px solid ${INK};
  b { font-size: 14px; letter-spacing: 0.02em; }
  strong { font-size: 24px; font-variant-numeric: tabular-nums; letter-spacing: -0.02em; }
`;

const Detail = styled.dl`
  margin: 0;
  padding: 12px 0;
  border-bottom: 1px solid ${LINE};
  &:last-of-type { border-bottom: 0; }
  dt {
    font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
    color: ${INK3}; margin-bottom: 3px;
  }
  dd { margin: 0; font-size: 14px; font-weight: 500; }
`;

const Muted = styled.span`display: block; font-weight: 400; color: ${INK2}; font-size: 13px;`;

const Cta = styled.button`
  width: 100%;
  margin-top: 18px;
  padding: 12px 14px;
  border: 0;
  border-radius: 10px;
  background: ${GREEN};
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`;

export default OrderConfirmation;
